import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import * as token from "@universityofmaryland/web-token-library";
import * as elementStyles from "@universityofmaryland/web-styles-library/element";
import * as typography from "@universityofmaryland/web-styles-library/typography";
import * as animation from "@universityofmaryland/web-styles-library/animation";
import { createMediaQuery } from "@universityofmaryland/web-utilities-library/styles";
import { parseDateFromElement } from "@universityofmaryland/web-utilities-library/date";
import { pin } from "@universityofmaryland/web-icons-library/location";
import { info } from "@universityofmaryland/web-icons-library/indicators";
import { theme } from "@universityofmaryland/web-utilities-library/theme";
import { wrapTextNodeInSpan } from "@universityofmaryland/web-utilities-library/dom";
import "@universityofmaryland/web-styles-library/layout";
import "@universityofmaryland/web-utilities-library/media";
import "@universityofmaryland/web-icons-library/controls";
import "@universityofmaryland/web-icons-library/communication";
import "@universityofmaryland/web-icons-library/files";
import "@universityofmaryland/web-icons-library/brand";
import "@universityofmaryland/web-icons-library/arrows";
import "@universityofmaryland/web-utilities-library/performance";
import "@universityofmaryland/web-utilities-library/accessibility";
import "../../atomic/animations/brand/card-stack.js";
import { createImageBackground } from "../../atomic/assets/image/background.js";
import "@universityofmaryland/web-styles-library";
import "@universityofmaryland/web-icons-library/calendar";
import "../../atomic/layout/block/stacked.js";
import "../../atomic/layout/person/columns.js";
import "@universityofmaryland/web-icons-library/social";
import "@universityofmaryland/web-utilities-library/string";
import "@universityofmaryland/web-utilities-library/animation";
import { createTextLockupSmall } from "../../atomic/text-lockup/small.js";
const mediumBreakpointStart = token.media.breakpointValues.medium.min;
const smallBreakpoint = token.media.breakpointValues.small.max;
const MakeDetailItem = (props) => {
  const { icon, text, isThemeDark } = props;
  const iconElement = new ElementBuilder("span").withHTML(icon).build();
  const textElement = new ElementBuilder("span").withHTML(text).build();
  return new ElementBuilder("p").styled(
    elementStyles.event.meta.composeItem({
      theme: isThemeDark ? "dark" : "light"
    })
  ).withStyles({
    element: {
      display: "flex",
      alignItems: "flex-start",
      [`& + .${elementStyles.event.meta.item.className}, & + .${elementStyles.event.meta.itemDark.className}`]: {
        marginTop: "5px"
      },
      ["& span "]: {
        marginLeft: "0 !important",
        marginTop: "0 !important"
      }
    }
  }).withChildren(iconElement, textElement).build();
};
const makeTimeRow = ({
  startTime,
  endTime,
  isThemeDark
}) => {
  const startParsed = parseDateFromElement({ element: startTime });
  if (!startParsed) return null;
  let timeText = startParsed.time;
  const endParsed = parseDateFromElement({ element: endTime ?? null });
  if (endParsed && endParsed.time !== startParsed.time) {
    timeText = `${timeText} - ${endParsed.time}`;
  }
  return new ElementBuilder("p").withClassName("card-event-time-row").styled(
    typography.sans.compose("small", {
      theme: isThemeDark ? "dark" : "light"
    })
  ).withStyles({
    element: {
      marginBottom: token.spacing.sm,
      textTransform: "uppercase",
      fontWeight: token.font.weight.bold
    }
  }).withHTML(timeText).build();
};
const makeMetaRow = ({
  location,
  information,
  isThemeDark
}) => {
  const children = [];
  if (location?.textContent) {
    children.push(
      MakeDetailItem({
        icon: pin,
        text: location.textContent,
        isThemeDark
      })
    );
  }
  if (information?.textContent) {
    children.push(
      MakeDetailItem({
        icon: info,
        text: information.textContent,
        isThemeDark
      })
    );
  }
  if (children.length === 0) return null;
  return new ElementBuilder().withClassName("card-event-time-meta").withStyles({
    element: {
      marginBottom: token.spacing.sm,
      "> *:not(:first-child)": {
        [`@container (min-width: 650px)`]: {
          width: "auto"
        }
      }
    }
  }).withChildren(...children).build();
};
const makeImageColumn = ({
  image,
  isAligned
}) => {
  const backgroundImage = createImageBackground({
    element: image,
    isScaled: isAligned,
    isGifAllowed: false,
    imageLoading: "lazy"
  });
  return new ElementBuilder().withClassName("card-event-time-image-wrapper").withStyles({
    element: {
      ...createMediaQuery("max-width", smallBreakpoint, {
        marginLeft: token.spacing.min,
        marginBottom: token.spacing.md,
        width: "120px",
        float: "right",
        position: "relative",
        zIndex: 99,
        ...isAligned && {
          height: "104px"
        }
      }),
      ...createMediaQuery("min-width", mediumBreakpointStart, {
        display: "block",
        width: "160px",
        order: "2",
        ...isAligned && {
          height: "160px"
        }
      }),
      "& img": {
        ...createMediaQuery("max-width", smallBreakpoint, {
          height: "auto !important"
        })
      }
    }
  }).withChild(backgroundImage).build();
};
const createCompositeCardEventTime = (props) => {
  const {
    headline,
    startTime,
    endTime,
    location,
    information,
    text,
    actions,
    image,
    isAligned,
    isThemeDark
  } = props;
  const contentChildren = [];
  const timeRow = makeTimeRow({ startTime, endTime, isThemeDark });
  if (timeRow) contentChildren.push(timeRow);
  if (headline) {
    const composeHeadlineStyles = {
      ...animation.line.composeSlideUnder({
        color: theme.foreground(isThemeDark)
      }),
      ...typography.sans.compose("larger", {
        theme: theme.fontColor(isThemeDark)
      })
    };
    const headlineElement = new ElementBuilder(headline).styled(composeHeadlineStyles).withStyles({
      element: {
        fontWeight: "700",
        [`& + *`]: {
          marginTop: token.spacing.sm
        }
      },
      subElement: {
        color: "currentColor"
      }
    }).withModifier((el) => wrapTextNodeInSpan(el)).build();
    contentChildren.push(headlineElement);
  }
  const metaRow = makeMetaRow({ location, information, isThemeDark });
  if (metaRow) contentChildren.push(metaRow);
  const lockup = createTextLockupSmall({
    text: text || null,
    actions: actions || null,
    isThemeDark
  });
  contentChildren.push(lockup);
  const contentColumn = new ElementBuilder().withClassName("card-event-time-content").withStyles({
    element: {
      flex: "1 0",
      ...createMediaQuery("min-width", mediumBreakpointStart, {
        paddingRight: token.spacing.md,
        order: "1"
      })
    }
  }).withChildren(...contentChildren).build();
  const children = [];
  if (image) {
    children.push(makeImageColumn({ image, isAligned }));
  }
  children.push(contentColumn);
  const wrapper = new ElementBuilder().withClassName("card-event-time-wrapper").withStyles({
    element: {
      containerType: "inline-size",
      ...createMediaQuery("min-width", mediumBreakpointStart, {
        display: "flex",
        justifyContent: "space-between"
      })
    }
  }).withChildren(...children).build();
  return new ElementBuilder().withClassName("card-event-time-container").withStyles({
    element: {
      height: "100%",
      overflow: "hidden",
      ...isThemeDark && {
        backgroundColor: token.color.black,
        color: token.color.white
      }
    }
  }).withChild(wrapper).build();
};
export {
  createCompositeCardEventTime
};
//# sourceMappingURL=event-time.js.map
